import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { signInWithCustomToken, signOut as firebaseSignOut, type User } from 'firebase/auth';
import { setOnAuthExpired, setTenant } from '../api/client';
import { ApiError } from '../api/envelope';
import { unregisterPushDevice } from '../api/comms';
import { releaseBackgroundPush } from '../lib/webNotify';
import { auth } from '../firebase';
import { validateClaims } from './claims';
import { AuthContext, type AuthState } from './context';
import { dispatchLogin } from './dispatchAuth';
import { redeemHandoff } from './handoff';

const TENANT_KEY = 'console.lastTenant';

function readLastTenant(): string {
  try {
    return localStorage.getItem(TENANT_KEY) ?? '';
  } catch {
    return '';
  }
}

async function resolveUser(user: User): Promise<{ tenant: string } | null> {
  const result = await user.getIdTokenResult();
  const claims = validateClaims(result.claims);
  return claims.ok ? { tenant: claims.realm } : null;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<AuthState['status']>('initializing');
  const [tenant, setTenantState] = useState<string | null>(null);
  const [email, setEmail] = useState<string | null>(null);
  const [lastTenant, setLastTenant] = useState(readLastTenant);

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((user) => {
      void (async () => {
        if (!user) {
          setTenant(null);
          setTenantState(null);
          setEmail(null);
          setStatus('signedOut');
          return;
        }
        // A persisted session from a non-admin or claimless token (or another
        // Firebase app sharing the project) is useless here — drop it.
        const resolved = await resolveUser(user).catch(() => null);
        if (!resolved) {
          await firebaseSignOut(auth).catch(() => {});
          return; // onAuthStateChanged fires again with null
        }
        setTenant(resolved.tenant);
        setTenantState(resolved.tenant);
        setEmail(user.email ?? null);
        setStatus('signedIn');
      })();
    });
    return unsubscribe;
  }, []);

  // The client calls this when a forced-refresh retry still bounces — the
  // session is truly dead, so surface the sign-in screen.
  useEffect(() => {
    setOnAuthExpired(() => {
      void firebaseSignOut(auth).catch(() => {});
    });
    return () => setOnAuthExpired(null);
  }, []);

  const signIn = useCallback(async (tenantKey: string, emailInput: string, password: string) => {
    const key = tenantKey.trim();
    // Kill any persisted session first — otherwise a tenant-A session survives
    // a tenant-B sign-in attempt that fails halfway.
    await firebaseSignOut(auth).catch(() => {});
    const customToken = await dispatchLogin(key, emailInput, password);
    const credential = await signInWithCustomToken(auth, customToken);
    const resolved = await resolveUser(credential.user).catch(() => null);
    if (!resolved) {
      await firebaseSignOut(auth).catch(() => {});
      throw new ApiError('forbidden', 1036, 'This account is not an admin for that tenant.');
    }
    try {
      localStorage.setItem(TENANT_KEY, key);
    } catch {
      // Storage may be unavailable (private mode) — prefill is best-effort.
    }
    setLastTenant(key);
    // onAuthStateChanged completes the state flip; nothing else to do here.
  }, []);

  /**
   * The dashboard opened us in a new tab with a one-time code. Same ending as
   * [signIn] — a real Firebase session on this origin — so everything
   * downstream (token refresh, the socket, an OAuth round trip) is identical to
   * having typed a password here.
   */
  const signInWithHandoff = useCallback(async (code: string) => {
    // Redeem FIRST. Firebase persistence is shared by every tab on this origin,
    // so signing out is felt by a console tab already open — and a code that
    // then fails to redeem (they last sixty seconds; a slow tab is enough)
    // cost the agent a session they had, in the tab they were working in.
    const session = await redeemHandoff(code);
    await firebaseSignOut(auth).catch(() => {});
    const credential = await signInWithCustomToken(auth, session.token);
    const resolved = await resolveUser(credential.user).catch(() => null);
    if (!resolved) {
      await firebaseSignOut(auth).catch(() => {});
      throw new ApiError('forbidden', 1036, 'This account is not an admin for that tenant.');
    }
    // Prefill the form with the tenant they arrived as, so a later expiry lands
    // on a sign-in screen that already knows where they belong.
    try {
      localStorage.setItem(TENANT_KEY, resolved.tenant);
    } catch {
      // Storage may be unavailable (private mode) — prefill is best-effort.
    }
    setLastTenant(resolved.tenant);
  }, []);

  const signOut = useCallback(async () => {
    // Release the push registration FIRST — it is an authenticated call, and
    // after firebaseSignOut there is no token left to make it with. Leaving it
    // behind would keep pushing this agent's conversations to this browser.
    await releaseBackgroundPush(unregisterPushDevice);
    await firebaseSignOut(auth).catch(() => {});
  }, []);

  const value = useMemo<AuthState>(
    () => ({ status, tenant, email, lastTenant, signIn, signInWithHandoff, signOut }),
    [status, tenant, email, lastTenant, signIn, signInWithHandoff, signOut],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
